class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.cachedResults = {};
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = '';

    this.setupEventListeners();
  }

  setupEventListeners() {
    this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));

    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
    this.addEventListener('click', this.onCoveraxOverlayClick.bind(this));
    this.addEventListener('pointerdown', this.onResultsPointerDown.bind(this));
    this.setupSwipeToClose();
  }

  isCoveraxOverlayResults() {
    const el = this.predictiveSearchResults;
    if (!el) return false;
    return (
      el.classList.contains('predictive-search--header-inline') ||
      el.classList.contains('predictive-search--header-mobile') ||
      el.classList.contains('predictive-search--hero')
    );
  }

  isBottomSearchOverlay() {
    return this.predictiveSearchResults?.classList.contains('predictive-search--header-mobile');
  }

  setupSwipeToClose() {
    const overlay = this.predictiveSearchResults;
    if (!overlay) return;

    let startY = 0;
    let startX = 0;
    let currentY = 0;
    let dragging = false;
    let locked = false;
    let panel = null;

    const resetSwipe = () => {
      dragging = false;
      locked = false;
      overlay.classList.remove('is-swiping');
      overlay.style.removeProperty('--coverax-swipe-y');
      const livePanel = panel || overlay.querySelector('.coverax-predictive');
      if (livePanel) livePanel.style.removeProperty('transform');
      overlay.style.removeProperty('opacity');
      panel = null;
    };

    const canStartSwipe = (eventTarget) => {
      if (!this.isOpen || !this.isBottomSearchOverlay()) return false;
      const scrollRoot = eventTarget.closest(
        '.coverax-predictive__main-scroll, .coverax-predictive__aside, .coverax-predictive__shell'
      );
      if (scrollRoot && scrollRoot.scrollTop > 0) return false;
      return Boolean(eventTarget.closest('.coverax-predictive'));
    };

    overlay.addEventListener(
      'touchstart',
      (event) => {
        if (event.touches.length !== 1) return;
        if (!canStartSwipe(event.target)) return;
        const touch = event.touches[0];
        startY = touch.clientY;
        startX = touch.clientX;
        currentY = startY;
        dragging = false;
        locked = false;
        panel = overlay.querySelector('.coverax-predictive');
      },
      { passive: true }
    );

    overlay.addEventListener(
      'touchmove',
      (event) => {
        if (!panel || event.touches.length !== 1) return;
        if (!this.isOpen || !this.isBottomSearchOverlay()) return;

        const touch = event.touches[0];
        const deltaY = touch.clientY - startY;
        const deltaX = Math.abs(touch.clientX - startX);

        if (!dragging) {
          if (locked) return;
          if (Math.abs(deltaY) < 8 && deltaX < 8) return;
          if (deltaY < 0 || deltaX > Math.abs(deltaY)) {
            locked = true;
            return;
          }
          dragging = true;
          overlay.classList.add('is-swiping');
        }

        if (!dragging) return;
        event.preventDefault();
        currentY = touch.clientY;
        const offset = Math.max(0, deltaY);
        panel.style.transform = `translate3d(0, ${offset}px, 0)`;
        overlay.style.opacity = String(Math.max(0.35, 1 - offset / 420));
      },
      { passive: false }
    );

    const endSwipe = () => {
      if (!dragging) {
        resetSwipe();
        return;
      }
      const offset = Math.max(0, currentY - startY);
      const shouldClose = offset > 110 || offset > window.innerHeight * 0.18;
      if (shouldClose) {
        this.clearAndCloseSearch();
        this.input?.blur();
      }
      resetSwipe();
    };

    overlay.addEventListener('touchend', endSwipe, { passive: true });
    overlay.addEventListener('touchcancel', endSwipe, { passive: true });
  }

  clearAndCloseSearch() {
    this.abortController.abort();
    this.abortController = new AbortController();
    this.searchTerm = '';
    this.close(true);
    this.toggleResetButton();
  }

  onCoveraxOverlayClick(event) {
    const closeBtn = event.target.closest('[data-coverax-search-close]');
    if (closeBtn) {
      event.preventDefault();
      this.clearAndCloseSearch();
      return;
    }

    // Klick auf abgedunkeltes Overlay (nicht auf Ergebnis-Panel) → Suche schließen
    const overlay = this.predictiveSearchResults;
    if (!overlay || !this.isOpen) return;
    if (!overlay.contains(event.target)) return;
    if (event.target.closest('.coverax-predictive')) return;

    event.preventDefault();
    this.clearAndCloseSearch();
  }

  onResultsPointerDown(event) {
    // Interaktion in den Ergebnissen (Info, Varianten, Links): Fokus im Suchfeld lassen,
    // sonst schließt focusout die ganze Suche
    if (event.target.closest('[data-coverax-search-close]')) return;
    if (!this.predictiveSearchResults?.contains(event.target)) return;
    // Backdrop: nicht offen halten – Klick schließt die Suche
    if (!event.target.closest('.coverax-predictive')) return;
    this.keepOpenOnFocusOut = true;

    // CTA zur Suchseite und normale Navigationslinks nicht blockieren
    if (event.target.closest('.coverax-predictive__search-for, a[href]')) {
      window.clearTimeout(this.keepOpenClearTimeout);
      this.keepOpenClearTimeout = window.setTimeout(() => {
        this.keepOpenOnFocusOut = false;
      }, 50);
      return;
    }

    if (event.target.closest('button, [data-option-pill], [data-info-toggle], [data-info-panel]')) {
      event.preventDefault();
    }
    window.clearTimeout(this.keepOpenClearTimeout);
    this.keepOpenClearTimeout = window.setTimeout(() => {
      this.keepOpenOnFocusOut = false;
    }, 50);
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    super.onChange();
    const newSearchTerm = this.getQuery();
    const keepStaleResults = this.isOpen && this.isCoveraxOverlayResults();
    if (!keepStaleResults && (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm))) {
      this.querySelector('#predictive-search-results-groups-wrapper')?.remove();
    }

    // Update the term asap, don't wait for the predictive search query to finish loading
    this.updateSearchForTerm(this.searchTerm, newSearchTerm);

    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      this.close(true);
      return;
    }

    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    // Coverax: Enter/Submit navigiert nicht zur Suchseite
    event.preventDefault();
  }

  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.searchTerm = '';
      this.abortController.abort();
      this.abortController = new AbortController();
      this.closeResults(true);
    }
  }

  onFocus() {
    const currentSearchTerm = this.getQuery();

    if (!currentSearchTerm.length) return;

    if (this.searchTerm !== currentSearchTerm) {
      // Search term was changed from other search input, treat it as a user change
      this.onChange();
    } else if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (this.keepOpenOnFocusOut) {
        this.keepOpenOnFocusOut = false;
        return;
      }
      const active = document.activeElement;
      if (this.contains(active) || this.predictiveSearchResults?.contains(active)) return;
      this.close();
    });
  }

  onKeyup(event) {
    if (!this.getQuery().length) this.close(true);
    event.preventDefault();

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up');
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        // Keine Aktion – Suche bleibt im Overlay
        break;
      case 'Escape': {
        // Zuerst offene Produkt-Infos schließen, Suche erst beim zweiten Escape
        const openInfo = this.predictiveSearchResults?.querySelector('.kp-bs-card.is-info-open, coverax-product-card.is-info-open');
        if (openInfo?.closeInfoPanel) {
          openInfo.closeInfoPanel();
          break;
        }
        this.abortController.abort();
        this.abortController = new AbortController();
        this.searchTerm = '';
        this.close(true);
        this.toggleResetButton();
        this.input.blur();
        break;
      }
    }
  }

  onKeydown(event) {
    // Cursor nicht bewegen bei Pfeiltasten; Enter nie absenden
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown' || event.code === 'Enter') {
      event.preventDefault();
    }
  }

  updateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.querySelector('[data-predictive-search-search-for-text]');
    const currentButtonText = searchForTextElement?.innerText;
    if (currentButtonText) {
      if (currentButtonText.match(new RegExp(previousTerm, 'g')).length > 1) {
        // The new term matches part of the button text and not just the search term, do not replace to avoid mistakes
        return;
      }
      const newButtonText = currentButtonText.replace(previousTerm, newTerm);
      searchForTextElement.innerText = newButtonText;
    }
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');

    // Filter out hidden elements (duplicated page and article resources) thanks
    // to this https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/offsetParent
    const allVisibleElements = Array.from(
      this.querySelectorAll('li, a.predictive-search__item, button.predictive-search__item')
    ).filter((element) => element.offsetParent !== null);
    let activeElementIndex = 0;

    if (moveUp && !selectedElement) return;

    let selectedElementIndex = -1;
    let i = 0;

    while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
      if (allVisibleElements[i] === selectedElement) {
        selectedElementIndex = i;
      }
      i++;
    }

    this.statusElement.textContent = '';

    if (!moveUp && selectedElement) {
      activeElementIndex = selectedElementIndex === allVisibleElements.length - 1 ? 0 : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex = selectedElementIndex === 0 ? allVisibleElements.length - 1 : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];

    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  selectOption() {
    const selectedOption = this.querySelector(
      '[aria-selected="true"] a, a[aria-selected="true"], button[aria-selected="true"]'
    );

    if (selectedOption) selectedOption.click();
  }

  getSearchResults(searchTerm) {
    // Cache-Prefix: alte Einträge ohne SKU-Fallback nicht wiederverwenden
    const queryKey = `sku-v3:${searchTerm.replace(/\s+/g, '-').toLowerCase()}`;

    // Vorherigen Request abbrechen, damit veraltete Antworten das Panel nicht neu rendern/schließen
    this.abortController.abort();
    this.abortController = new AbortController();

    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      const searchDeferred = this.dispatchSearchUpdateEvent(searchTerm);
      searchDeferred?.resolve({ totalCount: this.getTotalResultCount() });
      return;
    }

    const searchDeferred = this.dispatchSearchUpdateEvent(searchTerm);
    const requestTerm = searchTerm;
    const signal = this.abortController.signal;

    // Section OHNE resources[options][fields]: mit preview_theme_id (theme dev) sonst HTTP 422
    const sectionUrl = new URL(routes.predictive_search_url, window.location.origin);
    sectionUrl.searchParams.set('q', searchTerm);
    sectionUrl.searchParams.set('section_id', 'predictive-search');
    sectionUrl.searchParams.set('resources[limit]', '10');
    sectionUrl.searchParams.set('resources[limit_scope]', 'each');

    // JSON mit erweiterten Feldern: SKU/Barcode + Produktbeschreibung (auch in der Preview)
    const fields = [
      'title',
      'product_type',
      'variants.title',
      'vendor',
      'variants.sku',
      'variants.barcode',
      'body',
    ].join(',');
    const jsonUrl = new URL(`${routes.predictive_search_url}.json`, window.location.origin);
    jsonUrl.searchParams.set('q', searchTerm);
    jsonUrl.searchParams.set('resources[type]', 'product');
    jsonUrl.searchParams.set('resources[limit]', '10');
    jsonUrl.searchParams.set('resources[options][fields]', fields);

    Promise.all([
      fetch(sectionUrl.toString(), { signal }).then((response) => {
        if (!response.ok) throw new Error(response.status);
        return response.text();
      }),
      fetch(jsonUrl.toString(), { signal })
        .then((response) => (response.ok ? response.json() : null))
        .catch(() => null),
    ])
      .then(async ([text, jsonData]) => {
        if (requestTerm !== this.searchTerm) return;

        const doc = new DOMParser().parseFromString(text, 'text/html');
        const section = doc.querySelector('#shopify-section-predictive-search');
        if (!section) throw new Error('predictive-search section missing');

        const jsonProducts = jsonData?.resources?.results?.products || [];
        await this.mergeSkuSearchProducts(section, jsonProducts, searchTerm, signal);

        const resultsMarkup = section.innerHTML;
        this.allPredictiveSearchInstances.forEach((predictiveSearchInstance) => {
          predictiveSearchInstance.cachedResults[queryKey] = resultsMarkup;
        });
        this.renderSearchResults(resultsMarkup);

        searchDeferred?.resolve({ totalCount: this.getTotalResultCount() });
      })
      .catch((error) => {
        if (error?.name === 'AbortError' || error?.code === 20) {
          // Code 20 / AbortError: Request abgebrochen
          searchDeferred?.reject(error);
          return;
        }
        searchDeferred?.reject(error);
        this.close();
        throw error;
      });
  }

  sectionHasProductCards(section) {
    return Boolean(
      section.querySelector(
        '#predictive-search-results-products-list .kp-bs-card, #predictive-search-results-products-list coverax-product-card, #predictive-search-results-products-list .coverax-product-card'
      )
    );
  }

  async mergeSkuSearchProducts(section, jsonProducts, searchTerm, signal) {
    if (!jsonProducts.length) return;

    if (this.sectionHasProductCards(section)) {
      section.querySelectorAll('.kp-bs-card, coverax-product-card').forEach((card) => {
        card.dataset.searchQuery = searchTerm;
      });
      return;
    }

    // Volle Coverax-Karten nachladen: Section mit Produkttitel (ohne fields-Param, Preview-sicher)
    const titles = [...new Set(jsonProducts.map((product) => product.title).filter(Boolean))].slice(0, 10);
    const collectedItems = [];

    await Promise.all(
      titles.map(async (title) => {
        try {
          const titleUrl = new URL(routes.predictive_search_url, window.location.origin);
          titleUrl.searchParams.set('q', title);
          titleUrl.searchParams.set('section_id', 'predictive-search');
          titleUrl.searchParams.set('resources[limit]', '10');
          titleUrl.searchParams.set('resources[limit_scope]', 'each');
          const response = await fetch(titleUrl.toString(), { signal });
          if (!response.ok) return;
          const html = await response.text();
          const titleDoc = new DOMParser().parseFromString(html, 'text/html');
          const titleSection = titleDoc.querySelector('#shopify-section-predictive-search');
          if (!titleSection) return;

          titleSection.querySelectorAll('#predictive-search-results-products-list > li').forEach((item) => {
            const card = item.querySelector('.kp-bs-card, coverax-product-card');
            const href = card?.dataset.productUrl || item.querySelector('a[href*="/products/"]')?.getAttribute('href') || '';
            const matchesJson = jsonProducts.some((product) => {
              if (!product.handle) return false;
              return href.includes(`/products/${product.handle}`);
            });
            if (!matchesJson) return;

            const clone = item.cloneNode(true);
            const cloneCard = clone.querySelector('.kp-bs-card, coverax-product-card');
            if (cloneCard) cloneCard.dataset.searchQuery = searchTerm;

            const matched = jsonProducts.find((product) => href.includes(`/products/${product.handle}`));
            if (matched?.url && cloneCard) {
              this.rewriteCardProductUrl(cloneCard, matched.url);
            }

            collectedItems.push(clone);
          });
        } catch (error) {
          if (error?.name === 'AbortError' || error?.code === 20) throw error;
        }
      })
    );

    if (collectedItems.length > 0) {
      this.placeProductItems(section, collectedItems, searchTerm, jsonProducts.length);
      return;
    }

    this.injectSkuFallbackProducts(section, jsonProducts, searchTerm);
  }

  rewriteCardProductUrl(card, productUrl) {
    try {
      const next = new URL(productUrl, window.location.origin);
      const path = `${next.pathname}${next.search}${next.hash}`;
      card.dataset.productUrl = path;
      card.querySelectorAll('a[href*="/products/"]').forEach((anchor) => {
        anchor.href = path;
      });
    } catch (error) {
      // URL aus der Suggest-API konnte nicht gesetzt werden
    }
  }

  placeProductItems(section, items, searchTerm, jsonCount) {
    let list = section.querySelector('#predictive-search-results-products-list');
    if (!list) {
      const scroll = section.querySelector('.coverax-predictive__main-scroll') || section.querySelector('.coverax-predictive__main');
      if (!scroll) return;
      scroll.innerHTML = `
        <ul
          id="predictive-search-results-products-list"
          class="coverax-predictive__grid kp-bs list-unstyled"
          role="group"
          aria-labelledby="predictive-search-products"
        ></ul>
      `;
      list = scroll.querySelector('#predictive-search-results-products-list');
    }

    list.innerHTML = '';
    items.forEach((item, index) => {
      item.id = `predictive-search-option-product-${index + 1}`;
      list.appendChild(item);
    });

    section.querySelectorAll('.coverax-predictive__empty').forEach((el) => el.remove());
    section.querySelector('.coverax-predictive__shell')?.classList.remove('coverax-predictive__shell--empty');
    section
      .querySelector('.predictive-search__results-groups-wrapper')
      ?.classList.remove('predictive-search__results-groups-wrapper--no-products');

    this.updatePredictiveTotalResults(section, list.children.length, jsonCount, searchTerm);
  }

  injectSkuFallbackProducts(section, products, searchTerm) {
    const escapeHtml = (value) =>
      String(value ?? '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');

    const items = products
      .map((product, index) => {
        const variant = product.variants?.[0];
        const rawHref = String(product.url || variant?.url || `/products/${product.handle}`);
        const href = rawHref.startsWith('/') && !rawHref.startsWith('//') ? rawHref : '#';
        const variantTitle = variant?.title && variant.title !== 'Default Title' ? variant.title : '';
        const meta = [variantTitle, searchTerm ? `Art.-Nr. ${searchTerm}` : ''].filter(Boolean).join(' · ');
        return `
          <li
            id="predictive-search-option-product-${index + 1}"
            class="predictive-search__list-item coverax-predictive__grid-item"
            role="option"
            aria-selected="false"
          >
            <a href="${escapeHtml(href)}" class="coverax-predictive__sku-hit" tabindex="-1">
              <span class="coverax-predictive__sku-hit-title">${escapeHtml(product.title || '')}</span>
              ${meta ? `<span class="coverax-predictive__sku-hit-meta">${escapeHtml(meta)}</span>` : ''}
            </a>
          </li>
        `;
      })
      .join('');

    const scroll = section.querySelector('.coverax-predictive__main-scroll') || section.querySelector('.coverax-predictive__main');
    if (!scroll) return;

    scroll.innerHTML = `
      <ul
        id="predictive-search-results-products-list"
        class="coverax-predictive__grid coverax-predictive__grid--sku-fallback list-unstyled"
        role="group"
        aria-labelledby="predictive-search-products"
      >
        ${items}
      </ul>
    `;

    section.querySelectorAll('.coverax-predictive__empty').forEach((el) => el.remove());
    section.querySelector('.coverax-predictive__shell')?.classList.remove('coverax-predictive__shell--empty');
    this.updatePredictiveTotalResults(section, products.length, products.length, searchTerm);
  }

  updatePredictiveTotalResults(section, productCount, jsonCount, searchTerm) {
    const live = section.querySelector('[data-total-results]');
    if (!live) return;
    const current = parseInt(live.dataset.totalResults || '0', 10) || 0;
    // Section ohne Produkte zählt oft nur Sidebar-Treffer – Produkttreffer addieren
    const withoutProducts = Math.max(0, current);
    const next = withoutProducts + productCount;
    live.dataset.totalResults = String(next || jsonCount || productCount);
    if (productCount > 0 && /keine|no results/i.test(live.textContent || '')) {
      live.textContent = `${productCount} ${searchTerm}`;
    }
  }

  getTotalResultCount() {
    return parseInt(this.predictiveSearchResults.querySelector('[data-total-results]')?.dataset.totalResults) || 0;
  }

  dispatchSearchUpdateEvent(query) {
    const { SearchUpdateEvent } = window.StandardEvents || {};
    if (!SearchUpdateEvent) return null;

    const deferred = SearchUpdateEvent.createPromise();
    this.dispatchEvent(
      new SearchUpdateEvent({
        search: { query },
        promise: deferred.promise,
      })
    );
    return deferred;
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute('results', true);
    // open vor removeAttribute('loading'), sonst kurz weder [open] noch [loading]
    // → Overlay springt zu translateY(100%) und animiert erneut hoch
    this.open();
    this.setLiveRegionResults();
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    const liveRegion = this.querySelector('[data-predictive-search-live-region-count-value]');
    if (liveRegion) this.setLiveRegionText(liveRegion.textContent);
  }

  getResultsMaxHeight() {
    this.resultsMaxHeight =
      window.innerHeight - document.querySelector('.section-header')?.getBoundingClientRect().bottom;
    return this.resultsMaxHeight;
  }

  open() {
    // Coverax-Overlay: Höhe kommt aus CSS – kein inline maxHeight (verursacht Layout-Bugs)
    if (this.isCoveraxOverlayResults()) {
      this.predictiveSearchResults.removeAttribute('style');
      this.predictiveSearchResults.classList.add('is-open');
    } else {
      this.predictiveSearchResults.style.maxHeight = this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
    }
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
    }
    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    this.resultsMaxHeight = false;
    this.predictiveSearchResults.removeAttribute('style');
    this.predictiveSearchResults?.classList.remove('is-open', 'is-swiping');
  }
}

customElements.define('predictive-search', PredictiveSearch);
